import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from "@headlessui/react";
import { useModalAction } from "../modal/modal.context";
import { toast } from "react-toastify";
import SearchHint from './SearchHint';
import { useRecoilState, useRecoilValue } from 'recoil';
import { addNewCustomFabricAtom, addToProjectAtom } from '../../state';
import { PoweroffOutlined, ShoppingOutlined } from '@ant-design/icons';
import AlankaramLogo from '../../icons/alankaram';


interface IProps {
    isSearchBar?: boolean,
    isIcons?: boolean
}

const Header = (props: IProps) => {

    const { isSearchBar = true, isIcons = true } = props

    const user = localStorage.getItem('userData');
    const userData = user && JSON.parse(user);
    const { openModal } = useModalAction();
    const cartItems = useRecoilValue(addToProjectAtom);
    const itemCount = cartItems?.length || 0;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const navigate = useNavigate();

    // Reset mobile search when screen size changes to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) { // md breakpoint
                setShowMobileSearch(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (showMobileSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showMobileSearch]);

    const onLogout = () => {
        localStorage.removeItem('access-token');
        localStorage.removeItem('userData');
        toast.success('Logged out successfully')
        navigate('/')
    }

    const handleSearchSelect = (query: string) => {
        console.log('Selected search:', query);
    };

    const [, setAddNewCustomFabric] = useRecoilState(addNewCustomFabricAtom)

    return (
        <header className="w-full bg-white shadow-sm sticky top-0 z-10 px-3">
            <div className="mx-auto sm:px-6">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo or Mobile Search Bar */}
                    {showMobileSearch ? (
                        <div className="flex-1 flex items-center">
                            {isSearchBar && <SearchHint
                                onSearchSelect={handleSearchSelect}
                                inputRef={searchInputRef}
                            />}
                        </div>
                    ) : (
                        <div className="flex items-center flex-shrink-0 gap-2">
                            <div className="w-48 sm:w-64 md:w-80 lg:w-96 xl:w-[400px] flex-shrink-0 flex items-center cursor-pointer" onClick={() => {
                                setAddNewCustomFabric([])
                                navigate('/products')
                            }}>
                                <AlankaramLogo />
                            </div>
                        </div>
                    )}

                    {/* Search Bar - hidden on mobile when mobile search is active */}
                    {isSearchBar && <div className={`${showMobileSearch ? 'hidden' : 'hidden md:block'} flex-1 max-w-2xl`}>
                        <SearchHint onSearchSelect={handleSearchSelect} />
                    </div>}

                    {/* Mobile menu button */}
                    {isIcons && <div className="flex md:hidden items-center space-x-4">
                        {/* Mobile search icon */}
                        {!showMobileSearch ? (
                            <button className="md:hidden ml-2" onClick={() => setShowMobileSearch(true)}>
                                {/* Magnifying glass icon */}
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        ) : <button onClick={() => setShowMobileSearch(false)} className="ml-2 md:hidden">
                            {/* Close icon */}
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>}
                        <Link to="/orders" className="relative no-underline text-dark flex justify-center">
                            <ShoppingOutlined className="header_icon_shopping" />
                        </Link>
                        <Link to="/cart" className="relative no-underline text-dark">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {itemCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {itemCount}
                                </span>
                            )}
                        </Link>
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center ps-0 py-2 rounded-md text-black focus:outline-none"
                        >
                            <span className="sr-only">Open main menu</span>
                            {!isMobileMenuOpen ? (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>}

                    {/* Desktop menu */}
                    {isIcons && <div className="hidden md:flex items-center justify-end space-x-4">
                        <Link to="/cart" className="relative no-underline text-dark">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {itemCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {itemCount}
                                </span>
                            )}
                        </Link>
                        <Link to="/orders" className="relative no-underline text-dark">My Orders</Link>
                        <Menu as="div" className="relative inline-block text-left">
                            <Menu.Button className="flex items-center focus:outline-0">
                                {userData ? (
                                    <div className="bg-gray-200 py-2 px-3 rounded-full flex items-center">
                                        <span className="text-sm font-medium">{userData.name}</span>
                                    </div>
                                ) : (
                                    <button className="text-sm font-medium">Login</button>
                                )}
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items
                                    as="ul"
                                    className='ml-0 p-0 absolute mt-1 w-48 rounded bg-white shadow-700 focus:outline-none right-0 origin-top-right rtl:left-0 rtl:origin-top-left'
                                >
                                    <Menu.Item>
                                        {userData ?
                                            <li>
                                                <button
                                                    onClick={onLogout}
                                                    className='block w-full py-3 px-6 text-sm font-semibold capitalize text-heading transition-all duration-200 hover:text-accent hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50 text-left rtl:text-right flex items-center space-x-3 rounded-md'
                                                >
                                                    <PoweroffOutlined className="h-4 w-4 flex-shrink-0" />
                                                    <span>Logout</span>
                                                </button>
                                            </li>
                                            :
                                            <li>
                                                <button
                                                    onClick={() => openModal('LOGIN')}
                                                    className='block w-full py-2.5 px-6 text-sm font-semibold capitalize text-heading transition duration-200 hover:text-accent focus:outline-0 text-left rtl:text-right'
                                                >Login</button>
                                            </li>
                                        }
                                    </Menu.Item>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>}
                </div>

                {/* Mobile menu */}
                {isIcons && <Transition
                    show={isMobileMenuOpen}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <div className="md:hidden">
                        <div>
                            {/* Mobile menu items */}
                            <div>
                                {userData ? (
                                    <button
                                        onClick={onLogout}
                                        className="block w-full text-left px-4 py-3 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200 flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
                                    >
                                        <PoweroffOutlined className="h-4 w-4 flex-shrink-0" />
                                        <span>Logout</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => openModal('LOGIN')}
                                        className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                    >
                                        Login
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </Transition>}
            </div>
        </header>
    );
};

export default Header;
